import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PaymentDueAccountVM } from "@propertelligent/client-api/models/PaymentDueAccountVM";
import "./payment.css";
import H3 from "../typography/H3";
import PaymentOutstandingAmount from "./PaymentOutstandingAmount";
import EmptyState from "../emptystate";
import SingleDropdown from "../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../assets/svgs/Chevren_down";
import LargeTooltip from "../tooltips/large-tooltip";
import dateicon from "../../assets/Date.svg";
import docicon from "../../assets/Doc.svg";
import { GetImages } from "../../utils/GetImages";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import {
  BulkPayAmount,
  buildPartialBulkPayAmounts,
  resolveGroupedPaymentIds,
} from "../../services/paymentDueAccount.service";

interface PaymentPayablesORReceivablesListProps {
  paymentMode: string;
  payments: PaymentDueAccountVM[];
  selectedPayments: PaymentDueAccountVM[];
  setSelectedPayments: (
    payments: PaymentDueAccountVM[],
    totalAmount: number,
    amounts?: BulkPayAmount[],
  ) => void;
  setDateFilterType: (value: any) => void;
  setFullyQualifiedFilter: (value: any) => void;
  DATE_FILTERS: any;
}

interface PaymentState {
  mode: 'full' | 'partial' | null; // Added null
  partialAmount: number;
}

export default function PaymentPayablesORReceivablesList({
  paymentMode,
  payments,
  selectedPayments,
  setSelectedPayments,
  setDateFilterType,
  setFullyQualifiedFilter,
  DATE_FILTERS
}: PaymentPayablesORReceivablesListProps) {
  const currencySymbol = useCurrencySymbol();
  const paymentsRef = useRef(payments);
  paymentsRef.current = payments;

  const [selectAll, setSelectAll] = useState(false);

  const initialPaymentStates = useMemo(() => {
    return payments.reduce((acc, payment) => {
      acc[payment.id] = {
        mode: null, // Changed from 'full' to null
        partialAmount: payment.amount || 0
      };
      return acc;
    }, {} as Record<string, PaymentState>);
  }, []);

  const [paymentStates, setPaymentStates] = useState<Record<string, PaymentState>>(initialPaymentStates);

  const selectedPaymentIds = useMemo(
    () => new Set(selectedPayments.map(payment => payment.id)),
    [selectedPayments]
  );
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    description: string;
    position: { top: number; left: number };
  }>({
    isOpen: false,
    description: "",
    position: { top: 0, left: 0 },
  });

  const MENU_ITEMS = useMemo(
    () => [
      {
        label: "All Payments",
        action: () => {
          setDateFilterType(null);
          setFullyQualifiedFilter(paymentMode === "Receivable" ? "paidFlowTypeId == 2" : "paidFlowTypeId == 1");
        },
        isDivider: false,
        isDestructive: false,
      },
      {
        label: "Next 30 Days",
        action: () => setDateFilterType(DATE_FILTERS?.NEXT_30_DAYS),
        isDivider: false,
        isDestructive: false,
      },
      {
        label: "Next 3 months",
        action: () => setDateFilterType(DATE_FILTERS?.NEXT_3_MONTHS),
        isDivider: false,
        isDestructive: false,
      },
      {
        label: "Next 6 months",
        action: () => setDateFilterType(DATE_FILTERS?.NEXT_6_MONTHS),
        isDivider: false,
        isDestructive: false,
      },
    ],
    []
  );

  const calculateOutstanding = useCallback((paymentId: string) => {
    const payment = paymentsRef.current.find(p => p.id === Number(paymentId));
    if (!payment) return 0;

    const state = paymentStates[paymentId];
    if (!state) return payment.amount || 0;

    if (state.mode === 'partial') {
      return (payment.amount || 0) - (state.partialAmount || 0);
    }
    return 0;
  }, [paymentStates]);

  useEffect(() => {
    setSelectAll(selectedPayments.length === payments.length && payments.length > 0);
  }, [selectedPayments, payments]);

  useEffect(() => {
    setPaymentStates(prev => {
      const newStates = { ...prev };
      let hasChanges = false;

      payments.forEach(payment => {
        if (!newStates[payment.id]) {
          newStates[payment.id] = {
            mode: 'full',
            partialAmount: payment.amount || 0
          };
          hasChanges = true;
        }
      });

      if (payments.length < Object.keys(newStates).length) {
        Object.keys(newStates).forEach(id => {
          if (!payments.some(p => p.id === Number(id))) {
            delete newStates[id];
            hasChanges = true;
          }
        });
      }

      return hasChanges ? newStates : prev;
    });
  }, [payments]);

  const updateSelectedPayments = useCallback((
    updatedPayments: PaymentDueAccountVM[],
    states: Record<string, PaymentState> = paymentStates,
  ) => {
    const total = updatedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const amounts = buildPartialBulkPayAmounts(updatedPayments, states);
    setSelectedPayments(
      updatedPayments,
      total,
      amounts.length > 0 ? amounts : undefined,
    );
  }, [setSelectedPayments, paymentStates]);

  const handlePaymentModeChange = useCallback((paymentId: string, mode: 'full' | 'partial') => {
    const paymentIdNum = Number(paymentId);
    const payment = paymentsRef.current.find(p => p.id === paymentIdNum);

    // Multi-id grouped invoices cannot be partially allocated client-side.
    if (
      mode === "partial" &&
      resolveGroupedPaymentIds(payment ?? {}).length > 1
    ) {
      return;
    }

    const nextStates: Record<string, PaymentState> = {
      ...paymentStates,
      [paymentId]: {
        ...paymentStates[paymentId],
        mode,
        partialAmount: mode === 'partial'
          ? (paymentStates[paymentId]?.partialAmount || payment?.amount || 0)
          : payment?.amount || 0
      }
    };
    setPaymentStates(nextStates);

    if (selectedPaymentIds.has(paymentIdNum)) {
      const updated = selectedPayments.map(p => {
        if (p.id === paymentIdNum) {
          return {
            ...p,
            amount: mode === 'partial'
              ? nextStates[paymentId]?.partialAmount || 0
              : payment?.amount || 0
          };
        }
        return p;
      });
      updateSelectedPayments(updated, nextStates);
    }
  }, [paymentStates, selectedPaymentIds, selectedPayments, updateSelectedPayments]);

  const handlePartialAmountChange = useCallback((paymentId: string, value: string) => {

    const paymentIdNum = Number(paymentId);

    const nextStates: Record<string, PaymentState> = {
      ...paymentStates,
      [paymentId]: {
        ...paymentStates[paymentId],
        partialAmount: parseFloat(value) || 0.00
      }
    };
    setPaymentStates(nextStates);

    if (selectedPaymentIds.has(paymentIdNum)) {
      const updated = selectedPayments.map(p => {
        if (p.id === paymentIdNum) {
          return {
            ...p,
            amount: parseFloat(value) || 0.00
          };
        }
        return p;
      });
      updateSelectedPayments(updated, nextStates);
    }
  }, [paymentStates, selectedPaymentIds, selectedPayments, updateSelectedPayments]);

  const handleSelectAll = useCallback(() => {
    const newSelected = selectAll ? [] : paymentsRef.current.map(payment => {
      const isMultiId = resolveGroupedPaymentIds(payment).length > 1;
      const mode = isMultiId ? "full" : paymentStates[payment.id]?.mode;
      return {
        ...payment,
        amount: mode === 'partial'
          ? paymentStates[payment.id].partialAmount
          : payment.amount
      };
    });

    let statesForAmounts = paymentStates;
    if (!selectAll) {
      // Force multi-id rows off Partial so we never emit a misleading override.
      const forced: Record<string, PaymentState> = { ...paymentStates };
      let changed = false;
      paymentsRef.current.forEach(payment => {
        if (
          resolveGroupedPaymentIds(payment).length > 1 &&
          forced[payment.id]?.mode === "partial"
        ) {
          forced[payment.id] = {
            ...forced[payment.id],
            mode: "full",
            partialAmount: payment.amount || 0,
          };
          changed = true;
        }
      });
      if (changed) {
        setPaymentStates(forced);
        statesForAmounts = forced;
      }
    }

    updateSelectedPayments(newSelected, statesForAmounts);
  }, [selectAll, paymentStates, updateSelectedPayments]);

  const handlePaymentSelect = useCallback((payment: PaymentDueAccountVM) => {
    const isMultiId = resolveGroupedPaymentIds(payment).length > 1;
    let statesForAmounts = paymentStates;

    if (!selectedPaymentIds.has(payment.id) && isMultiId && paymentStates[payment.id]?.mode === "partial") {
      statesForAmounts = {
        ...paymentStates,
        [payment.id]: {
          ...paymentStates[payment.id],
          mode: "full",
          partialAmount: payment.amount || 0,
        },
      };
      setPaymentStates(statesForAmounts);
    }

    const mode = isMultiId
      ? "full"
      : statesForAmounts[payment.id]?.mode;

    const updated = selectedPaymentIds.has(payment.id)
      ? selectedPayments.filter(p => p.id !== payment.id)
      : [...selectedPayments, {
        ...payment,
        amount: mode === 'partial'
          ? statesForAmounts[payment.id].partialAmount
          : payment.amount
      }];

    updateSelectedPayments(updated, statesForAmounts);
  }, [selectedPayments, selectedPaymentIds, paymentStates, updateSelectedPayments]);

  const handleViewMoreClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    description: string
  ) => {

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipState({
      isOpen: true,
      description,
      position: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      },
    });
  };

  const handleCloseTooltip = () => {
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };

  const paymentItems = useMemo(() => {
    return payments.map((payment, index) => {
      const isChecked = selectedPaymentIds.has(payment.id);
      const isMultiIdGroup = resolveGroupedPaymentIds(payment).length > 1;
      const paymentState = paymentStates[payment.id] || {
        mode: null, // Changed from 'full' to null
        partialAmount: payment.amount || 0
      };
      const effectiveMode =
        isMultiIdGroup && paymentState.mode === "partial"
          ? "full"
          : paymentState.mode;
      const outstandingAmount = calculateOutstanding(String(payment.id));

      return (
        <>

          <div key={payment.id}>
            <div className="payment-wrapper position-relative">
              <div className="d-block" style={{ paddingRight: '100px' }}>
                <div className="d-flex gap-2 align-items-center mb-2">
                  <input
                    className="payment-checkbox"
                    type="checkbox"
                    id={`checkbox-${payment.id}`}
                    checked={isChecked}
                    onChange={() => handlePaymentSelect(payment)}
                    aria-labelledby={`label-${payment.id}`}
                  />
                  <h2 id={`label-${payment.id}`} className="address-title">
                    {payment?.streetAddress || "Organisation"}
                  </h2>
                </div>

                <div className="info-line">
                  <img
                  src={docicon}
                  alt=""
                  width={20}
                  height={20}
                  className="info-line-icon"
                  loading="lazy"
                />
                  <span className="payment-desc">
                    {payment.description || "No description available"}
                  </span>
                  {payment.description.length > 30 && (
                    <a
                      href="#"
                      className="listing-viewmore mx-2 ml-1 text-blue-500 underline"
                      onClick={(e) => handleViewMoreClick(e, payment.description)}
                    >
                      View more
                    </a>
                  )}
                </div>
                
                <div className="info-line">
                  <img
                  src={dateicon}
                  alt=""
                  width={20}
                  height={20}
                  className="info-line-icon"
                  loading="lazy"
                />
                  <span className="due-text">
                    Due on: {payment.formatted?.DueDateDateOnly || "No due date specified"}
                  </span>
                </div>
              </div>

              <div className="payment-radio-align-right">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`radio-${payment.id}`}
                    id={`full-${payment.id}`}
                    value="full"
                    checked={isChecked && effectiveMode === 'full'} // Only checked if checkbox is selected
                    onChange={() => handlePaymentModeChange(String(payment.id), 'full')}
                    disabled={!isChecked} // Optional: disable when checkbox is not checked
                  />
                  <label className="form-check-label" htmlFor={`full-${payment.id}`}>
                    Full
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`radio-${payment.id}`}
                    id={`partial-${payment.id}`}
                    value="partial"
                    checked={isChecked && effectiveMode === 'partial'} // Only checked if checkbox is selected
                    onChange={() => handlePaymentModeChange(String(payment.id), 'partial')}
                    disabled={!isChecked || isMultiIdGroup}
                    title={
                      isMultiIdGroup
                        ? "Partial payment isn't supported for grouped invoices"
                        : undefined
                    }
                  />
                  <label className="form-check-label" htmlFor={`partial-${payment.id}`}>
                    Partial
                  </label>
                </div>
              </div>

              {effectiveMode === 'full' ? (
                <div className="amount-text">
                  <CurrencyTooltip
                    content={payment.formatted?.AmountRaw}
                    formatted={payment.formatted?.Amount}
                  >
                  <span>{payment.formatted?.Amount || 0}</span>
                  </CurrencyTooltip>
                </div>
              ) : (

                <div className="partial-input-group">
                  <span className="partial-input-group-text"><b>{currencySymbol}</b></span>
                  <input
                    type="number"
                    className="form-control partial-amount-input"
                    value={paymentState.partialAmount}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          handlePartialAmountChange(String(payment.id), value);
                        }
                      }}
                    placeholder="Enter amount"
                    step="0.01"
                    min="0"
                    
                  />
                </div>
              )}
            </div>

            {effectiveMode === 'partial' && (
              <PaymentOutstandingAmount
                label="Outstanding Amount"
                amount={outstandingAmount.toString()}
              />
            )}

            {index < payments.length - 1 && <div className="separator-line"></div>}
          </div>
        </>
      );
    });
  }, [payments, selectedPaymentIds, paymentStates, calculateOutstanding, handlePaymentSelect, handlePaymentModeChange, handlePartialAmountChange]);

  return (
    <div className="main-container">
      <LargeTooltip
        isOpen={tooltipState.isOpen}
        onClose={handleCloseTooltip}
        description={tooltipState.description}
        position={tooltipState.position}
      />


      <div className="header-section d-flex align-items-center justify-space-between pb-2 pt-3 m-0">
        <H3 text={`${payments.length} Payments`} />
        <SingleDropdown
          triggerText="All Payments"
          triggerIcon={<Chevren_downIcon width={"18"} height={"18"} />}
          triggerIconPosition="right"
          menuItems={MENU_ITEMS}
        />
      </div>
      {payments.length > 0 ? (
        <div className="main-wrapper">
          <div className="checkbox-wrapper">
            <input
              className="payment-checkbox"
              type="checkbox"
              id="selectAllCheckbox"
              checked={selectAll}
              onChange={handleSelectAll}
              aria-label={selectAll ? "Deselect all payments" : "Select all payments"}
            />
            <label className="checkbox-inline-label" htmlFor="selectAllCheckbox">
              Select All
            </label>
          </div>

          <div className="separator-line"></div>

          <div className="payments-list">
            {paymentItems}
          </div>
        </div>
      ) : (
        <EmptyState
          icon={GetImages.emptyStatePayment}
          heading="No Payments Found"
          isButton={false}
          className="empty-state-wrapper"
        />
      )}
    </div>
  );
}
